// PositionCalculatorPage — Risk / Position size calculator
// Layout: account size, risk %, entry, stop → computed position size

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TfCardList, TfSectionHeaderView } from '../../components/TfPrimitives';

export function PositionCalculatorPage() {
  const nav = useNavigate();
  const [account, setAccount] = useState(50000);
  const [riskPct, setRiskPct] = useState(1);
  const [entry, setEntry] = useState(5280);
  const [stop, setStop] = useState(5260);
  const [target, setTarget] = useState(5340);
  const [instrument, setInstrument] = useState<'ES' | 'NQ' | 'CL' | 'GC' | 'BTC'>('ES');

  const INSTRUMENT_META: Record<string, { tickSize: number; tickValue: number; name: string; emoji: string; color: string }> = {
    ES:  { tickSize: 0.25,  tickValue: 12.50, name: 'E-mini S&P',   emoji: '📈', color: '#007AFF' },
    NQ:  { tickSize: 0.25,  tickValue: 5.00,  name: 'E-mini NQ',    emoji: '⚡', color: '#5856D6' },
    CL:  { tickSize: 0.01,  tickValue: 10.00, name: 'Crude Oil',     emoji: '🛢️', color: '#1C1C1E' },
    GC:  { tickSize: 0.10,  tickValue: 10.00, name: 'Gold',          emoji: '🥇', color: '#FF9500' },
    BTC: { tickSize: 5.00,  tickValue: 5.00,  name: 'Bitcoin',       emoji: '₿',  color: '#FF3B30' },
  };

  const m = INSTRUMENT_META[instrument];
  const riskDollar = (account * riskPct) / 100;
  const stopDistance = Math.abs(entry - stop);
  const targetDistance = Math.abs(target - entry);
  const stopTicks = stopDistance / m.tickSize;
  const targetTicks = targetDistance / m.tickSize;
  const positionSize = stopTicks > 0 ? Math.floor(riskDollar / (stopTicks * m.tickValue)) : 0;
  const reward = positionSize * targetTicks * m.tickValue;
  const rr = stopTicks > 0 ? targetTicks / stopTicks : 0;

  return (
    <div className="min-h-screen bg-tf-surface-1 pb-8">
      <div className="sticky top-0 z-10 flex h-11 items-center justify-between border-b border-tf-border bg-tf-bg/85 px-2 backdrop-blur-xl">
        <button onClick={() => nav(-1)} data-testid="back" className="flex h-9 w-9 items-center justify-center text-tf-blue">
          <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2.4"><path d="M15 5l-7 7 7 7" strokeLinecap="round" strokeLinejoin="round" /></svg>
        </button>
        <div className="absolute left-1/2 -translate-x-1/2 text-[15px] font-semibold text-tf-text-1">Position calculator</div>
        <button onClick={() => { setEntry(5280); setStop(5260); setTarget(5340); }} className="px-3 text-[15px] text-tf-blue">Reset</button>
      </div>

      {/* Hero — result */}
      <div className="mx-4 mt-4">
        <div className="rounded-[20px] text-center p-5 text-white" style={{ background: 'linear-gradient(135deg, #007AFF 0%, #5856D6 100%)' }}>
          <div className="text-[11px] uppercase tracking-wider text-white/80">Position size</div>
          <div className="mt-1 text-[40px] font-bold tabular-nums leading-none">{positionSize}</div>
          <div className="text-[12px] text-white/85">contracts of {instrument}</div>
          <div className="mt-3 grid grid-cols-3 gap-2">
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Risk</div>
              <div className="text-[14px] font-bold tabular-nums text-white">${riskDollar.toFixed(0)}</div>
            </div>
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">Reward</div>
              <div className="text-[14px] font-bold tabular-nums text-tf-profit">+${reward.toFixed(0)}</div>
            </div>
            <div className="rounded-[12px] bg-white/15 backdrop-blur-md p-2.5">
              <div className="text-[10px] text-white/70 uppercase">R:R</div>
              <div className="text-[14px] font-bold tabular-nums text-white">1:{rr.toFixed(1)}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Instrument selector */}
      <TfSectionHeaderView title="Instrument" />
      <div className="mx-4 flex gap-1.5 overflow-x-auto">
        {Object.entries(INSTRUMENT_META).map(([k, v]) => (
          <button
            key={k}
            onClick={() => setInstrument(k as any)}
            data-testid={`instr-${k}`}
            className={['shrink-0 rounded-full px-3 py-2 text-[12px] font-semibold transition',
              instrument === k ? 'bg-tf-text-1 text-white' : 'bg-tf-bg border border-tf-border text-tf-text-1 active:opacity-40'
            ].join(' ')}
          >
            <span className="mr-1">{v.emoji}</span>{k}
          </button>
        ))}
      </div>

      {/* Account + risk */}
      <TfSectionHeaderView title="Account" />
      <div className="mx-4">
        <TfCardList>
          <NumberRow label="Account size" value={account} onChange={setAccount} prefix="$" testid="calc-account" />
          <NumberRow label="Risk per trade" value={riskPct} onChange={setRiskPct} suffix="%" sub="0.5% – 2% recommended" testid="calc-risk" last />
        </TfCardList>
      </div>

      {/* Trade levels */}
      <TfSectionHeaderView title="Trade levels" />
      <div className="mx-4">
        <TfCardList>
          <NumberRow label="Entry price"  value={entry}  onChange={setEntry}  step={m.tickSize} testid="calc-entry" />
          <NumberRow label="Stop loss"    value={stop}   onChange={setStop}   step={m.tickSize} sub={`${stopTicks.toFixed(0)} ticks away`} testid="calc-stop" />
          <NumberRow label="Take profit"  value={target} onChange={setTarget} step={m.tickSize} sub={`${targetTicks.toFixed(0)} ticks away`} testid="calc-target" last />
        </TfCardList>
      </div>

      {/* Trade setup summary */}
      <TfSectionHeaderView title="Summary" />
      <div className="mx-4">
        <TfCardList>
          <Row label="Instrument"  value={m.name} />
          <Row label="Tick size"   value={m.tickSize} />
          <Row label="Tick value"  value={`$${m.tickValue.toFixed(2)}`} />
          <Row label="Stop distance" value={`${stopDistance.toFixed(2)} pts (${stopTicks.toFixed(0)} ticks)`} />
          <Row label="Risk per contract" value={`$${(stopTicks * m.tickValue).toFixed(2)}`} />
          <Row label="Position"     value={`${positionSize} contracts`} bold />
          <Row label="Total risk"   value={`$${riskDollar.toFixed(2)} (${riskPct}% of account)`} bold color="text-tf-loss" />
          <Row label="Total reward" value={`$${reward.toFixed(2)}`} bold color="text-tf-profit" />
          <Row label="Risk:Reward"  value={`1 : ${rr.toFixed(2)}`} last bold />
        </TfCardList>
      </div>

      {/* Pro tip callout */}
      <div className="mx-4 mt-3 rounded-[14px] border border-tf-profit/30 bg-tf-profit/[0.04] p-3">
        <div className="flex gap-2.5">
          <span className="text-[18px]">💡</span>
          <div>
            <div className="text-[13px] font-semibold text-tf-profit">Pro tip</div>
            <p className="mt-0.5 text-[12px] text-tf-text-1">Aim for 1:2 risk:reward minimum. Even with 40% win rate, you'll be profitable.</p>
          </div>
        </div>
      </div>

      {/* Calculate CTA */}
      <div className="mx-4 mt-4">
        <button
          data-testid="calc-button"
          className="h-12 w-full rounded-full bg-tf-text-1 text-[16px] font-semibold text-white active:scale-[0.99]"
        >
          Calculate
        </button>
      </div>
    </div>
  );
}

function NumberRow({ label, value, onChange, prefix, suffix, sub, step, testid, last }: { label: string; value: number; onChange: (v: number) => void; prefix?: string; suffix?: string; sub?: string; step?: number; testid?: string; last?: boolean }) {
  return (
    <div data-testid={testid} className={['flex items-center justify-between px-4 py-3', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <div>
        <div className="text-[14px] font-medium text-tf-text-1">{label}</div>
        {sub && <div className="text-[11px] text-tf-text-3">{sub}</div>}
      </div>
      <div className="flex items-center gap-1.5">
        {prefix && <span className="text-[14px] text-tf-text-3">{prefix}</span>}
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(Number(e.target.value))}
          step={step || 1}
          data-testid={`input-${testid}`}
          className="h-9 w-24 rounded-[10px] border border-tf-border bg-tf-surface-1 px-2 text-right text-[15px] font-bold tabular-nums text-tf-text-1 focus:border-tf-blue focus:outline-none focus:ring-2 focus:ring-tf-blue/30"
        />
        {suffix && <span className="text-[14px] text-tf-text-3">{suffix}</span>}
      </div>
    </div>
  );
}

function Row({ label, value, bold, color, last }: { label: string; value: string | number; bold?: boolean; color?: string; last?: boolean }) {
  return (
    <div className={['flex items-center justify-between px-4 py-2.5', last ? '' : 'border-b border-tf-border'].join(' ')}>
      <span className="text-[12px] text-tf-text-3">{label}</span>
      <span className={['tabular-nums', bold ? 'text-[15px] font-bold' : 'text-[13px] font-medium', color || 'text-tf-text-1'].join(' ')}>{value}</span>
    </div>
  );
}
